import cn from 'classnames'
import classNames from 'classnames'
import {
  MouseEvent,
  MouseEventHandler,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { PopupPositionContext } from 'core-components/library/popup'

import { Translation } from '../../../../features/translations'
import { ItemTag } from '../itemTag/ItemTag'

import { getDisplayValues } from './helpers/getDisplayValues'
import { ISelectToggleProps } from './models/selectToggleProps'

import styles from './SelectToggle.module.scss'

export const SelectToggle = ({
  selectedValues,
  label,
  onRemoveItem,
  onRemoveAll,
  disabled,
  isOpened,
}: ISelectToggleProps) => {
  const PositionContext = useContext(PopupPositionContext)
  /** -------------------- Variables ----------------------- */
  const ref = useRef<HTMLSpanElement>(null)
  const [showSelectedCountLabel, setShowSelectedCountLabel] = useState(false)

  const displayedValues = useMemo(() => {
    return getDisplayValues(selectedValues)
  }, [selectedValues])

  /** -------------------- Methods ----------------------- */
  const closePopupOnMouseDown = useCallback<MouseEventHandler<HTMLSpanElement>>(
    (e) => {
      e.stopPropagation()
      e.preventDefault()
      PositionContext.action.close()
    },
    [PositionContext.action]
  )

  const onPanelMouseDown = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!isOpened) {
        return
      }

      e.stopPropagation()
      e.preventDefault()

      PositionContext.action.close()
    },
    [PositionContext.action, isOpened]
  )

  const handleRemoveAll = useCallback<MouseEventHandler<HTMLSpanElement>>(
    (e) => {
      e.stopPropagation()
      onRemoveAll()
    },
    [onRemoveAll]
  )

  /** -------------------- Effects ----------------------- */
  useEffect(() => {
    if (ref.current) {
      const scrollWidth = ref.current.scrollWidth
      const clientWidth = ref.current.clientWidth

      setShowSelectedCountLabel(displayedValues.length > 0 && scrollWidth > clientWidth)
    }
  }, [ref, displayedValues])

  return (
    <div
      onMouseDown={onPanelMouseDown}
      className={classNames(styles.root, { [styles.disabled]: disabled })}
    >
      <span
        className={cn(styles.selectValues, {
          [styles.hideContainer]: showSelectedCountLabel,
        })}
        ref={ref}
      >
        {displayedValues.map((it) => (
          <span key={it.itemId} onMouseDown={closePopupOnMouseDown}>
            <ItemTag item={it} onCrossIconClick={onRemoveItem} />
          </span>
        ))}
      </span>

      <span
        className={cn(styles.label, {
          [styles.hideLabel]: displayedValues.length !== 0 && !showSelectedCountLabel,
          [styles.hasSelectedValues]: showSelectedCountLabel,
        })}
      >
        {displayedValues.length === 0 && (
          <span>{typeof label === 'string' ? <Translation text={label} span /> : label}</span>
        )}

        {showSelectedCountLabel && `${displayedValues.length} items selected`}
      </span>

      {displayedValues.length > 0 && (
        <span
          onMouseDown={closePopupOnMouseDown}
          className={styles.clearAll}
          onClick={handleRemoveAll}
        >
          <i className="kbase kbase-close-circle" />
        </span>
      )}

      <span className={styles.caretIcon}>
        {isOpened ? (
          <i className="kbase kbase-omni-chevron-up" />
        ) : (
          <i className="kbase kbase-omni-chevron-down" />
        )}
      </span>
    </div>
  )
}
